import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { ExternalHeaderComponent } from '../../../shared/external-header/external-header.component';
import { FooterComponent } from '../../../shared/footer/footer.component';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { BookConsultationModalComponent } from '../../../modals/book-consultation-modal/book-consultation-modal.component';
import { SuccessRegistrationModalComponent } from '../../../modals/success-registration-modal/success-registration-modal.component';
import { JourneyService } from '../../../shared/services/journey.service';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import {
  IJourney,
  IJourneyStep,
  JourneyStepStatus,
  JourneyStepType,
} from '../../../../assets/types/dtoTypes';
import { CountryService } from "../../../shared/services/country.service";
import { CountryCode, PRICING } from "./pricing.config";

@Component({
  selector: 'app-pricing-page',
  templateUrl: './pricing-page.component.html',
  styleUrls: ['./pricing-page.component.scss'],
  standalone: true,
  imports: [ExternalHeaderComponent, FooterComponent, MatIcon, MatProgressSpinner, TranslateModule, NgFor, NgIf, NgClass],
})
export class PricingPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly journeyService = inject(JourneyService);
  private readonly authService = inject(AuthenticationService);
  private readonly countryService = inject(CountryService);

  country: CountryCode = 'EU';
  readonly StepStatus = JourneyStepStatus;
  readonly StepType = JourneyStepType;

  journey: IJourney = this.defaultJourney();
  loading = false;

  private get userId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.loading = true;

    this.countryService.getCountry().subscribe((country) => {
      this.country = country;

      console.log(this.country);
      const userId = this.authService.getIdFromAccessToken();

      if (!userId) {
        this.journey = this.defaultJourney();
        this.loading = false;
        return;
      }

      this.journeyService.getJourneyById(userId).subscribe({
        next: (journey) => {
          this.journey = journey;
          this.loading = false;
        },
        error: () => {
          this.journey = this.defaultJourney();
          this.loading = false;
        },
      });
    });
  }

  goToMain(): void {
    this.openSuccessModal();
  }

  bookConsultation(): void {
    const ref = this.dialog.open(BookConsultationModalComponent, {
      maxWidth: '960px',
      width: '100%',
      panelClass: 'book-consultation-panel',
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.openSuccessModal();
      }
    });
  }

  private openSuccessModal(): void {
    this.dialog.open(SuccessRegistrationModalComponent, {
      panelClass: 'success-modal-panel',
      disableClose: true,
      data: { userId: this.userId },
    });
  }

  stepKey(step: IJourneyStep): string {
    switch (step.type) {
      case JourneyStepType.PROFILE: return 'step1';
      case JourneyStepType.CONSULTATION: return 'step2';
      case JourneyStepType.EMPLOYER_MATCH: return 'step3';
      case JourneyStepType.VISA: return 'step4';
    }
  }

  formatBookingDate(step: IJourneyStep): string {
    if (!step.bookingDate) return '';
    return new Date(step.bookingDate).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
    });
  }

  formatBookingTime(step: IJourneyStep): string {
    if (!step.bookingDate) return '';
    const t = new Date(step.bookingDate).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return t.replace(' AM', 'am').replace(' PM', 'pm');
  }

  showsVatNote(step: IJourneyStep): boolean {
    return this.country === 'TN' && step.type !== JourneyStepType.PROFILE;
  }

  formatPrice(step: IJourneyStep): string {
    const localized = this.getLocalizedPrice(step);

    const locale =
      this.country === 'TN'
        ? 'fr-TN'
        : this.country === 'MA'
          ? 'fr-MA'
          : this.country === 'IN'
            ? 'en-IN'
            : 'de-DE';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: localized.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(localized.price);
  }

  private getLocalizedPrice(step: IJourneyStep) {
    switch (step.type) {
      case JourneyStepType.PROFILE:
        return {
          currency:
            this.country === 'TN'
              ? 'TND'
              : this.country === 'MA'
                ? 'MAD'
                : this.country === 'IN'
                  ? 'INR'
                  : 'EUR',
          price: 0,
        };

      case JourneyStepType.CONSULTATION:
        return PRICING.basic[this.country];

      case JourneyStepType.EMPLOYER_MATCH:
        return PRICING.premium[this.country];

      case JourneyStepType.VISA:
        return PRICING.full_package[this.country];
    }
  }

  private defaultJourney(): IJourney {
    return {
      id: '',
      userId: '',
      currentStepOrder: 2,
      steps: [
        { id: '1', type: JourneyStepType.PROFILE, order: 1, priceCents: 0, currency: 'EUR', status: JourneyStepStatus.COMPLETED },
        { id: '2', type: JourneyStepType.CONSULTATION, order: 2, priceCents: 9900, currency: 'EUR', status: JourneyStepStatus.AVAILABLE, recommended: true },
        { id: '3', type: JourneyStepType.EMPLOYER_MATCH, order: 3, priceCents: 50000, currency: 'EUR', status: JourneyStepStatus.LOCKED },
        { id: '4', type: JourneyStepType.VISA, order: 4, priceCents: 100000, currency: 'EUR', status: JourneyStepStatus.LOCKED },
      ],
    };
  }
}
